import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import useScreenType from "components/ProjectNonSpecificComponents/Hooks/useScreenType"
import { HEIGHT_WITH_NAV_AND_FOOTER } from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { observer } from "mobx-react"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router"
import ConfigSectionButtons from "../Elements/Navigation/atoms/ConfigSectionButtons"
import concatWithSpaces from "components/ProjectNonSpecificComponents/Functions/concatWithSpaces"
import InputMask from "react-input-mask"
import Input from "components/ProjectNonSpecificComponents/Elements/Input"
import { usePTKMStore } from "../Contexts/PTKMStoreContext"
import { TDay, TptkmConfig, TWeekDay } from "../Stores/PTKMStore/ptkmStore"
import rmSpaces from "components/ProjectNonSpecificComponents/Functions/rmSpaces"
import DateInput from "../Elements/PTKM/molecules/DateInput"
import MediumButton from "../Elements/Global/atoms/Buttons/MediumButton"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import AppRequests from "../API/AppRequests/AppRequests"
import { useStore } from "../Contexts/GlobalStoreContext"

interface Props {}

const PTKMCalendarConfig = observer(({}: Props) => {
	const history = useHistory()
	const store = useStore()
	const { isMobile } = useScreenType()
	const { t } = useTranslation()
	const ptkmStore = usePTKMStore()
	useEffect(() => {
		const getPtkmConfig = async () => {
			const { calendar } = await ptkmStore.fetchSettings()

			if (calendar) {
				ptkmStore.editCalendar({ ...calendar })
			} else {
				store.setInfoModalConfig({
					type: "fail",
					titleKey: "settings.failedTotal",
					descriptionKey: "settings.totalFail",
				})
			}
		}
		getPtkmConfig()
	}, [])

	const saveChanges = async () => {
		const { ok } = await AppRequests("PTKM").post("save-ptkm-calendar", {
			...ptkmStore.calendar,
		})

		if (ok) {
			ptkmStore.fetchSettings(true)
			store.setInfoModalConfig({
				type: "info",
				titleKey: "data-saving.success",
				showDuration: 1000,
			})
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
	}

	return (
		<HyperDiv height={HEIGHT_WITH_NAV_AND_FOOTER} style={{ overflowY: "auto" }}>
			<ConfigSectionButtons />

			<HyperDiv padding={isMobile ? "" : "0 5rem"} centered>
				<HyperDiv direction="column" width="95vw" style={{ maxWidth: 600 }}>
					<CalendarContainer label={t("rawText.working-hours")}>
						<WorkingHours />
					</CalendarContainer>
					<CalendarContainer label={t("rawText.date-exceptions")} transperent>
						<DateInput label={t("rawText.date-exceptions")} section="holidays" />
						<DateInput
							label={t("rawText.moved-date")}
							section="transferedDays"
							// withTime
						/>
					</CalendarContainer>

					<MediumButton onClick={saveChanges} style={{ margin: "0px auto" }} className="w-48">
						{t("rawText.saveChanges")}
					</MediumButton>

					<Whitespace x="5" />
				</HyperDiv>
			</HyperDiv>
		</HyperDiv>
	)
})

export default PTKMCalendarConfig

type TContainerProps = {
	label: string
	children: any
	transperent?: boolean
}

export const CalendarContainer = ({ label, children, transperent = false }: TContainerProps) => {
	return (
		<div id="calendar-container" className="w-full mt-12">
			<label className="text-left font-bold pl-4">{label}</label>
			<div
				className={concatWithSpaces(
					transperent ? "" : "bg-gray-200",
					"rounded-lg w-full mt-2 p-2 pb-8 pt-4",
					"flex flex-col items-center"
				)}
			>
				{children}
			</div>
		</div>
	)
}

export const WorkingHours = observer(() => {
	const { t } = useTranslation()

	const WEEK: TWeekDay[] = [
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
		"sunday",
	]

	const ptkmStore = usePTKMStore()

	return (
		<>
			{WEEK.map((day) => {
				const isWeekend = day === "saturday" || day === "sunday"
				return (
					<div key={day} className="mt-2">
						<p className=" text-xs pb-1">
							{t(`date-and-time.${day}`)}
							{`${isWeekend ? "" : "*"}`}
						</p>
						<div className="flex flex-row items-center" id="hour-input">
							<InputMask
								mask="99:99"
								placeholder="hh:mm"
								value={ptkmStore.calendar?.week[day]?.start ?? ""}
								onChange={(e) => {
									const value = e.target.value
									const dayData: TDay = {
										...ptkmStore.calendar?.week[day],
										start: value,
									}
									ptkmStore.editWorkingHours(day, dayData)
								}}
							>
								{(inputProps: any) => (
									<Input {...inputProps} style={{ maxWidth: 120, textAlign: "center" }} />
								)}
							</InputMask>

							<label className="pl-3 pr-3">{" - "}</label>

							<InputMask
								mask="99:99"
								placeholder="hh:mm"
								value={ptkmStore.calendar?.week[day]?.end ?? ""}
								onChange={(e) => {
									const value = rmSpaces(e.target.value)
									const dayData: TDay = {
										...ptkmStore.calendar?.week[day],
										end: value,
									}
									ptkmStore.editWorkingHours(day, dayData)
								}}
							>
								{(inputProps: any) => (
									<Input {...inputProps} style={{ maxWidth: 120, textAlign: "center" }} />
								)}
							</InputMask>
						</div>
					</div>
				)
			})}
		</>
	)
})
